/**
 * AppShell.jsx
 * Path: frontend/src/components/AppShell.jsx
 * Description: Wspolny layout aplikacji po zalogowaniu z gornym paskiem i rozwijanym menu.
 * Related: pages/*.jsx, components/TeflisLogo.jsx
 */

import { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, LogOut, Menu, Users, X } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import { useAuth } from "../hooks/useAuth";
import buildInfo from "../generated/buildInfoRuntime";
import TeflisLogo from "./TeflisLogo";

export default function AppShell({ title, subtitle, actions, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const hasHeroText = Boolean(title || subtitle);
  const menuContextLabel = title || "Teflis";

  const navItems = useMemo(() => {
    const items = [
      {
        to: "/calendar",
        icon: CalendarDays,
        label: "Harmonogram",
      },
    ];

    if (String(user?.role || "").toLowerCase() === "admin") {
      items.push({
        to: "/admin/users",
        icon: Users,
        label: "Uzytkownicy",
      });
    }

    return items;
  }, [user?.role]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div className="app-shell">
      {menuOpen ? <button type="button" className="topbar-menu-scrim" aria-label="Zamknij menu" onClick={() => setMenuOpen(false)} /> : null}

      <header className={`topbar ${menuOpen ? "topbar-open" : ""}`}>
        <Link to="/calendar" className="brand-block brand-block-link" aria-label="Teflis">
          <TeflisLogo className="brand-logo" />
          {title ? (
            <div>
              <div className="brand-meta">{title}</div>
            </div>
          ) : null}
        </Link>

        <div className="topbar-controls">
          <div className="user-chip user-chip-compact">
            <strong>{user?.name || user?.email}</strong>
            <span>{String(user?.role || "worker").toUpperCase()}</span>
          </div>
          <button
            type="button"
            className="nav-link nav-link-button topbar-menu-button"
            onClick={() => setMenuOpen((current) => !current)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
            <span className="topbar-menu-text">Menu</span>
          </button>
        </div>

        <div className={`topbar-menu-panel ${menuOpen ? "topbar-menu-panel-open" : ""}`}>
          <div className="topbar-panel-user">
            <span className="topbar-panel-eyebrow">{menuContextLabel}</span>
            <strong>{user?.name || user?.email}</strong>
            <span>{String(user?.role || "worker").toUpperCase()}</span>
          </div>
          <nav className="topbar-menu-nav" aria-label="Nawigacja aplikacji">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-link menu-item ${isActive ? "nav-link-active" : ""}`}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
            <button type="button" className="nav-link nav-link-button menu-item" onClick={handleLogout}>
              <LogOut size={16} />
              <span>Wyloguj</span>
            </button>
          </nav>
          <div className="topbar-menu-meta">
            <span>Wersja {buildInfo.version || "dev"}</span>
          </div>
        </div>
      </header>

      <main className={`page-wrap ${hasHeroText ? "" : "page-wrap-compact"}`}>
        {hasHeroText ? (
          <section className="page-hero">
            <div>
              <h1>{title}</h1>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            {actions ? <div className="hero-side">{actions}</div> : null}
          </section>
        ) : actions ? (
          <section className="page-toolbar-bar">
            <div className="page-toolbar-bar-inner">{actions}</div>
          </section>
        ) : null}

        {children}
      </main>

      <footer className="footer-bar">
        <span>Wersja {buildInfo.version || "dev"}</span>
      </footer>
    </div>
  );
}
